/**
 * 크롬 줄의 열린 플랜. 피그마의 파일 탭과 같이 작업 세트만 보여 준다.
 * 탭을 누르면 그 플랜의 마지막 화면으로 들어가고, 왼쪽은 그 플랜의 메뉴가 된다.
 * 탭 줄의 빈 곳은 창을 옮긴다. 버튼은 그대로 누른다.
 */
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { Board16Regular, Dismiss12Regular, Home16Regular } from "@fluentui/react-icons";
import { useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useSurfaceLocation } from "./settings-url";
import { planVisibilityOf, showInPlanList, usePlanVisibilityTick } from "@/features/access/plan-visibility";
import { useWorkspaceRole } from "@/features/access/workspace-access";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { dismissPlanTab, planAfterClose, planTabFitCount, planTabPath, PLAN_TAB_HOME_PX, PLAN_TAB_WIDTH_PX, splitPlanTabs } from "./plan-tabs";
import { projectIdFromPath } from "./project-nav";
import { labPlanTabs, useTabLabCount } from "./tab-lab";

const useStyles = makeStyles({
  row: {
    display: "flex",
    alignItems: "flex-end",
    gap: "2px",
    minWidth: "120px",
    flex: "1 1 160px",
    height: "100%",
    overflow: "hidden",
  },
  tab: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    width: `${PLAN_TAB_WIDTH_PX}px`,
    minWidth: `${PLAN_TAB_WIDTH_PX}px`,
    maxWidth: `${PLAN_TAB_WIDTH_PX}px`,
    flex: `0 0 ${PLAN_TAB_WIDTH_PX}px`,
    height: "32px",
    padding: "0 4px 0 10px",
    boxSizing: "border-box",
    borderRadius: "6px 6px 0 0",
    color: tokens.colorNeutralForeground3,
    position: "relative",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      color: tokens.colorNeutralForeground2,
    },
    "&:hover [data-plan-tab-close]": { opacity: 1 },
    "&:focus-within [data-plan-tab-close]": { opacity: 1 },
  },
  tabHome: {
    width: `${PLAN_TAB_HOME_PX}px`,
    minWidth: `${PLAN_TAB_HOME_PX}px`,
    maxWidth: `${PLAN_TAB_HOME_PX}px`,
    flex: `0 0 ${PLAN_TAB_HOME_PX}px`,
    padding: "0 10px",
  },
  tabOn: {
    backgroundColor: tokens.colorNeutralBackground2,
    color: tokens.colorNeutralForeground1,
    zIndex: 1,
    boxShadow: `inset 0 1px 0 ${tokens.colorNeutralStroke2}, inset 1px 0 0 ${tokens.colorNeutralStroke2}, inset -1px 0 0 ${tokens.colorNeutralStroke2}`,
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground2,
      color: tokens.colorNeutralForeground1,
    },
    "& [data-plan-tab-close]": { opacity: 1 },
  },
  name: {
    display: "flex",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
    height: "100%",
    padding: 0,
    border: "none",
    background: "transparent",
    color: "inherit",
    font: "inherit",
    fontSize: "13px",
    lineHeight: "18px",
    fontWeight: tokens.fontWeightRegular,
    textAlign: "left",
    gap: "6px",
    cursor: "pointer",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
      borderRadius: tokens.borderRadiusMedium,
    },
  },
  label: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  mark: {
    display: "inline-flex",
    flexShrink: 0,
    alignItems: "center",
  },
  close: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "16px",
    height: "16px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    background: "transparent",
    color: tokens.colorNeutralForeground3,
    opacity: 0,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      opacity: 1,
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  more: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    alignSelf: "flex-end",
    width: "28px",
    minWidth: "28px",
    height: "32px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground3,
    font: "inherit",
    fontSize: "16px",
    lineHeight: "16px",
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
});

type TabItem = { id: string; name: string; real: boolean };

function labelTruncated(el: HTMLElement | null) {
  return !!el && el.scrollWidth > el.clientWidth + 1;
}

function TabNameButton({
  name,
  icon,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { name: string; icon?: ReactNode }) {
  const styles = useStyles();
  const labelRef = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <Tooltip
      content={name}
      relationship="label"
      positioning="below"
      visible={open}
      onVisibleChange={(_, data) => setOpen(data.visible && labelTruncated(labelRef.current))}
    >
      <button type="button" className={className} {...props}>
        {icon ? <span className={styles.mark}>{icon}</span> : null}
        <span ref={labelRef} className={styles.label}>
          {name}
        </span>
      </button>
    </Tooltip>
  );
}

function focusPlan(navigate: (path: string) => void, planId: string) {
  const catalog = useCatalogStore.getState();
  if (!catalog.projects[planId]) return;
  const store = useProjectStore.getState();
  if (store.activeId !== planId) store.openProject(planId);
  navigate(planTabPath(planId, catalog.byProject[planId]?.lastTab));
}

export function PlanTabBar() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { pathname } = useSurfaceLocation();
  const routePlanId = projectIdFromPath(pathname);
  const tabsRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const labCount = useTabLabCount();
  const [labId, setLabId] = useState<string | null>(null);

  const workspaceId = useCatalogStore((s) => s.activeWorkspaceId);
  const openIds = useCatalogStore((s) => s.workspaces[s.activeWorkspaceId]?.openPlanIds);
  const projects = useCatalogStore((s) => s.projects);
  const previewGuest = useProjectStore((s) => s.previewGuest);
  const guestPlanId = useProjectStore((s) => s.previewGuestPlanId);
  const role = useWorkspaceRole();
  const visibilityTick = usePlanVisibilityTick();

  const realItems = useMemo<TabItem[]>(
    () =>
      (openIds ?? [])
        .filter((id) => {
          if (!projects[id]) return false;
          if (previewGuest) return id === guestPlanId;
          return showInPlanList(planVisibilityOf(id), role).shown;
        })
        .map((id) => ({ id, name: projects[id]?.name ?? "플랜", real: true })),
    [openIds, projects, previewGuest, guestPlanId, role, visibilityTick],
  );
  const items = useMemo<TabItem[]>(
    () => (labCount ? labPlanTabs(labCount).map((tab) => ({ ...tab, real: false })) : realItems),
    [labCount, realItems],
  );

  const inPlan = !!routePlanId && !!projects[routePlanId];
  const activeId = labCount ? labId : inPlan ? routePlanId : null;
  const homeOn = !activeId;
  const limit = width > 0 ? planTabFitCount(width, items.length) : 0;
  const { shown, overflow } = splitPlanTabs(
    items.map((item) => item.id),
    activeId,
    limit,
  );
  const byId = useMemo(() => new Map(items.map((item) => [item.id, item])), [items]);

  useEffect(() => {
    const node = tabsRef.current;
    if (!node) return;
    const measure = () => setWidth(node.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [items.length]);

  useEffect(() => {
    setLabId(null);
  }, [labCount]);

  const openItem = (item: TabItem) => {
    if (!item.real) {
      setLabId(item.id);
      return;
    }
    focusPlan(navigate, item.id);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Tab" || event.altKey || !(event.metaKey || event.ctrlKey)) return;
      if (items.length < 1) return;
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
      event.preventDefault();
      const ids = items.map((item) => item.id);
      const current = ids.indexOf(activeId ?? "");
      const direction = event.shiftKey ? -1 : 1;
      const origin = current < 0 ? (direction > 0 ? -1 : 0) : current;
      const next = items[(origin + direction + items.length) % items.length];
      if (next) openItem(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items, activeId, navigate]);

  const closeTab = (planId: string) => {
    if (labCount) return;
    const ids = realItems.map((item) => item.id);
    const neighbor = planAfterClose(ids, planId);
    dismissPlanTab(planId);
    if (routePlanId !== planId) return;
    if (!neighbor || !projects[neighbor]) {
      navigate("/");
      return;
    }
    focusPlan(navigate, neighbor);
  };

  const onTabsKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const key = event.key;
    if (key !== "ArrowLeft" && key !== "ArrowRight" && key !== "Home" && key !== "End" && key !== "Delete" && key !== "Backspace") return;
    const tabs = Array.from(tabsRef.current?.querySelectorAll<HTMLButtonElement>("[data-plan-tab]") ?? []);
    const index = tabs.findIndex((tab) => tab === document.activeElement || tab.contains(document.activeElement));
    if (index < 0) return;
    if (key === "Delete" || key === "Backspace") {
      event.preventDefault();
      const planId = tabs[index]?.dataset.planId;
      if (planId) closeTab(planId);
      return;
    }
    event.preventDefault();
    const nextIndex = key === "Home" ? 0 : key === "End" ? tabs.length - 1 : index + (key === "ArrowRight" ? 1 : -1);
    const next = tabs[(nextIndex + tabs.length) % tabs.length];
    next?.focus();
  };

  return (
    <div ref={tabsRef} className={styles.row} role="tablist" aria-label="열린 플랜" data-plan-tabs="" data-tauri-drag-region="" data-workspace={workspaceId} onKeyDown={onTabsKey}>
      <div className={mergeClasses(styles.tab, styles.tabHome, homeOn && styles.tabOn)} data-tauri-drag-region="">
        <TabNameButton
          name="워크스페이스"
          icon={<Home16Regular />}
          className={styles.name}
          role="tab"
          data-plan-tab=""
          data-no-drag=""
          aria-selected={homeOn}
          tabIndex={homeOn ? 0 : -1}
          onClick={() => {
            setLabId(null);
            navigate("/");
          }}
        />
      </div>
      {shown.map((id) => {
        const item = byId.get(id);
        if (!item) return null;
        const selected = id === activeId;
        return (
          <div key={id} className={mergeClasses(styles.tab, selected && styles.tabOn)} data-tauri-drag-region="">
            <TabNameButton
              name={item.name}
              icon={<Board16Regular />}
              className={styles.name}
              role="tab"
              data-plan-tab=""
              data-plan-id={item.real ? item.id : undefined}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => openItem(item)}
              onAuxClick={(event) => {
                if (event.button !== 1 || !item.real) return;
                event.preventDefault();
                closeTab(item.id);
              }}
            />
            {item.real && (
              <Tooltip content="닫기" relationship="label" positioning="below">
                <button
                  type="button"
                  className={styles.close}
                  data-plan-tab-close=""
                  aria-label={`${item.name} 닫기`}
                  tabIndex={selected ? 0 : -1}
                  onClick={(event) => {
                    event.stopPropagation();
                    closeTab(item.id);
                  }}
                >
                  <Dismiss12Regular />
                </button>
              </Tooltip>
            )}
          </div>
        );
      })}
      {overflow.length > 0 && (
        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <button type="button" className={styles.more} aria-label={`다른 플랜 ${overflow.length}개`}>
              …
            </button>
          </MenuTrigger>
          <MenuPopover>
            <MenuList aria-label="다른 열린 플랜">
              {overflow.map((id) => {
                const item = byId.get(id);
                if (!item) return null;
                return (
                  <MenuItem key={id} icon={<Board16Regular />} onClick={() => openItem(item)}>
                    {item.name}
                  </MenuItem>
                );
              })}
            </MenuList>
          </MenuPopover>
        </Menu>
      )}
    </div>
  );
}
